import { subscribeEvents } from '../events';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api';
import type { Server } from '../api';
import { Section } from './primitives';
import { Icon } from './icons';
import { ErrorNotice } from './ui';
import { actionLabel } from './operation/actionLabels';

/**
 * Where the install stands. `expired` and `check_stalled` are about waiting,
 * not about causes: the command's 15 minutes passed without the agent
 * connecting, or the server check was asked for and no result arrived.
 */
export type AgentInstallState = 'waiting' | 'expired' | 'connected' | 'auditing' | 'check_stalled' | 'complete';

/** The agent has registered, whatever the check did after. */
export function agentConnected(state: AgentInstallState): boolean {
  return state === 'connected' || state === 'auditing' || state === 'check_stalled' || state === 'complete';
}

/** How long the install command is valid; the panel says nothing connected within it. */
export const INSTALL_COMMAND_LIFETIME_MS = 15 * 60 * 1000;
/** How long the server check may take, from the agent connecting, before the panel says it has not finished. */
export const SERVER_CHECK_WAIT_MS = 3 * 60 * 1000;

export interface ReadinessSummaryCounts {
  passed: number;
  warnings: number;
  failed: number;
}

interface AgentInstallPanelProps {
  serverId: string;
  /** Absent for a server created on a cloud provider: its first boot runs the installer. */
  installationCommand?: string;
  /** Called on every state change so a parent (the project wizard) can
   * gate its own "Continue" on the agent being connected. */
  onStateChange?: (state: AgentInstallState, details: { server: Partial<Server> | null; summary: ReadinessSummaryCounts | null }) => void;
}

/**
 * AgentInstallPanel is the one place that explains how a server joins
 * Commerce Ops: run a single-use install command on the server, the
 * Commerce Ops Agent connects outbound, and the server check runs by
 * itself. It listens to the organization's event stream so the page moves
 * from "waiting" to "connected" to "checked" without a refresh. Used by the
 * Servers page and by the project wizard's server step.
 */
export function AgentInstallPanel({ serverId, installationCommand, onStateChange }: AgentInstallPanelProps) {
  const [state, setState] = useState<AgentInstallState>('waiting');
  // A new command replaces the one the parent passed (after it expired).
  const [command, setCommand] = useState<string | undefined>(installationCommand);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<'command' | 'check' | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [copied, setCopied] = useState(false);
  const [connectedServer, setConnectedServer] = useState<Partial<Server> | null>(null);
  const [summary, setSummary] = useState<ReadinessSummaryCounts | null>(null);

  useEffect(() => setCommand(installationCommand), [installationCommand]);

  const copyCommand = useCallback(() => {
    if (command) navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [command]);

  // Waiting has a limit. The command lives 15 minutes; past that, saying
  // "waiting" would be saying something the page cannot know.
  useEffect(() => {
    if (state !== 'waiting' && state !== 'connected' && state !== 'auditing') return;
    const limit = state === 'waiting' ? INSTALL_COMMAND_LIFETIME_MS : SERVER_CHECK_WAIT_MS;
    const timer = setTimeout(() => setState(state === 'waiting' ? 'expired' : 'check_stalled'), limit);
    return () => clearTimeout(timer);
  }, [state, attempt]);

  async function newCommand() {
    setBusy('command');
    setActionError(null);
    try {
      const res = await api.generateServerInstallToken(serverId);
      setCommand(res.installation_command);
      setState('waiting');
      setAttempt((value) => value + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(null);
    }
  }

  async function retryCheck() {
    setBusy('check');
    setActionError(null);
    try {
      await api.triggerServerAudit(serverId);
      setState('auditing');
      setAttempt((value) => value + 1);
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(null);
    }
  }

  useEffect(() => {
    onStateChange?.(state, { server: connectedServer, summary });
    // onStateChange is a notification callback; parents pass fresh closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, connectedServer, summary]);

  useEffect(
    () =>
      subscribeEvents((data) => {
        if (data.serverId !== serverId) return;
        if (data.type === 'server.connected') {
          setConnectedServer({
            hostname: data.hostname,
            os: data.os,
            architecture: data.architecture,
            agent_version: data.agentVersion,
          });
          setState((prev) => (prev === 'waiting' || prev === 'expired' ? 'connected' : prev));
        } else if (data.type === 'readiness.started') {
          setState('auditing');
          setAttempt((value) => value + 1);
        } else if (data.type === 'readiness.completed') {
          if (data.summary) setSummary(data.summary);
          setState('complete');
        }
      }),
    [serverId],
  );

  return (
    <div className="agent-install onboarding-stack">
      {command ? (
      <Section
        title="Install command"
        description="Run it on the server as root, or with sudo."
        actions={
          <button type="button" className="button button--secondary button--small" onClick={copyCommand}>
            <Icon name={copied ? 'check' : 'copy'} size={16} />
            {copied ? 'Copied' : 'Copy command'}
          </button>
        }
      >
        <pre className="command-block onboarding-command" data-testid="install-command">
          <code>{command}</code>
        </pre>
        <p className="onboarding-note">
          Single-use token, expires in 15 minutes.
          Requires Ubuntu 22.04/24.04 or Debian 12 (x86-64, arm64) with Docker and the compose plugin installed.
        </p>
      </Section>
      ) : null}

      <Section title="Connection">
        <ol className="agent-install__steps onboarding-install-steps" aria-live="polite">
          <InstallStep
            tone={state === 'waiting' ? 'active' : state === 'expired' ? 'problem' : 'done'}
            title={
              state === 'waiting'
                ? 'Waiting for the agent to connect…'
                : state === 'expired'
                  ? 'The agent has not connected'
                  : `Agent connected${connectedServer?.hostname ? ` from ${connectedServer.hostname}` : ''}`
            }
            detail={
              state === 'waiting'
                ? command ? undefined : 'Starting on Hetzner'
                : state === 'expired'
                  ? 'Expired after 15 minutes. The server must be able to reach Commerce Ops over HTTPS.'
                  : [connectedServer?.os, connectedServer?.architecture, connectedServer?.agent_version ? `agent v${connectedServer.agent_version}` : null]
                      .filter(Boolean)
                      .join(' · ')
            }
            actions={
              state === 'expired' ? (
                <button type="button" className="button button--secondary button--small" onClick={newCommand} disabled={busy !== null} data-testid="install-new-command">
                  {busy === 'command' ? 'Generating' : 'Generate new install command'}
                </button>
              ) : undefined
            }
          />
          <CheckStep state={state} summary={summary} busy={busy === 'check'} onRetry={retryCheck} />
        </ol>
        <ErrorNotice error={actionError} />
      </Section>
    </div>
  );
}

/** The server check row: done only when it finished and found nothing failed. */
function CheckStep({ state, summary, busy, onRetry }: { state: AgentInstallState; summary: ReadinessSummaryCounts | null; busy: boolean; onRetry: () => void }) {
  const readOnly = 'Read-only';
  if (state === 'check_stalled') {
    return (
      <InstallStep
        tone="problem"
        title="The server check has not finished"
        detail="No result received. Cause unknown."
        actions={
          <button type="button" className="button button--secondary button--small" onClick={onRetry} disabled={busy} data-testid="install-retry-check">
            {busy ? 'Starting' : actionLabel.retry}
          </button>
        }
      />
    );
  }
  if (state === 'complete') {
    if (!summary) {
      return <InstallStep tone="unknown" title="Server check finished" detail="Result not received" />;
    }
    const counts = `${summary.passed} passed · ${summary.warnings} ${summary.warnings === 1 ? 'warning' : 'warnings'} · ${summary.failed} failed`;
    if (summary.failed > 0) {
      return (
        <InstallStep
          tone="problem"
          title={`Server check found ${summary.failed} ${summary.failed === 1 ? 'problem' : 'problems'}`}
          detail={counts}
        />
      );
    }
    return <InstallStep tone="done" title="Server checked" detail={counts} />;
  }
  if (state === 'auditing' || state === 'connected') {
    return <InstallStep tone="active" title="Checking the server…" detail={readOnly} />;
  }
  return <InstallStep tone="pending" title="Server check" detail={readOnly} />;
}

type InstallTone = 'done' | 'active' | 'pending' | 'problem' | 'unknown';

const toneWords: Record<InstallTone, string> = {
  done: ' — done',
  active: ' — in progress',
  pending: ' — not started',
  problem: ' — needs attention',
  unknown: ' — finished, result not shown',
};

/** One row of the connection list: its state as an icon and in words. */
function InstallStep({ tone, title, detail, actions }: { tone: InstallTone; title: string; detail?: string; actions?: ReactNode }) {
  return (
    <li className={`onboarding-install-step onboarding-install-step--${tone}`}>
      <span className="onboarding-install-step__mark">
        {tone === 'done' ? (
          <Icon name="check-circle" size={16} />
        ) : tone === 'active' ? (
          <Icon name="spinner" size={16} />
        ) : tone === 'problem' ? (
          <Icon name="alert-circle" size={16} />
        ) : tone === 'unknown' ? (
          <Icon name="info" size={16} />
        ) : (
          <span className="onboarding-install-step__ring" aria-hidden="true" />
        )}
      </span>
      <div className="onboarding-install-step__text">
        <span className="onboarding-install-step__title">
          {title}
          <span className="visually-hidden">{toneWords[tone]}</span>
        </span>
        {detail ? <span className="onboarding-install-step__detail">{detail}</span> : null}
        {actions ? <span className="onboarding-install-step__actions">{actions}</span> : null}
      </div>
    </li>
  );
}
